import { isHomeViewOrganization } from '@upright/common/iam/organizations/home-view-organization-types';
import { OrganizationType } from '@upright/common/iam/organizations/organization-types';
// Type-only, so the unit-test runner never loads the hook module behind it.
import type { FrontPage } from '@upright/ui/views/home-views/front-page-config';

/**
 * Which unified shell a user belongs in, before the opt-in is asked: the investor one, the company
 * one (PLT-6943), or none.
 *
 * **The organisation type decides, not the user groups** (PLT-8105). The type is set per customer on
 * purpose and says what kind of customer it is; the segment groups behind `useFrontPageConfig` mix
 * licences with history. Reading the groups sent Company organisations whose users are also in an
 * investor group, or only in the general Clients group, to the investor home, which lists only the
 * investor types and Other (`isHomeViewOrganization`), so they met "This page isn't available for
 * your organization". It also left organisations in no client group at all with no shell to offer,
 * whatever their opt-in.
 *
 * - Company: the company shell.
 * - The types the investor home serves (`isHomeViewOrganization`: PE/VC, AM/AO and the deprecated
 *   asset-manager and asset-owner types, Other, and Internal for the team): the investor shell.
 * - Every other type (DMA Freemium and DMA Consultant until the DMA move, Arvo, and the types no
 *   organisation uses): none. Those shells are their own and this gate does not touch them.
 * - No organisation: the group rule, as before, so a client outside every organisation keeps the
 *   investor shell its front page gives it.
 *
 * The groups still decide everything else: licence, data coverage, features, chat and the opt-in
 * itself. The legacy shell's front page stays on the groups until that shell is removed.
 *
 * Pure so the decision is unit-tested; `useUnifiedShellAudience` feeds it.
 */
export type UnifiedShellAudience = 'investor' | 'company' | null;

export function unifiedShellAudience(args: {
  frontPage: FrontPage;
  organizationType: OrganizationType | undefined;
}): UnifiedShellAudience {
  if (args.organizationType === undefined) return args.frontPage === 'investor' ? 'investor' : null;
  if (args.organizationType === OrganizationType.COMPANY) return 'company';
  if (isHomeViewOrganization({ type: args.organizationType })) return 'investor';
  return null;
}
